import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, ExternalLinkIcon, ScanTextIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { ContentFormRenderer } from '@/components/content/content-form-renderer';
import { ScanDialog } from '@/components/content/scan-dialog';
import { VersionHistoryDrawer } from '@/components/content/version-history-drawer';
import { PermissionGate } from '@/components/permission-gate';
import { EnvironmentSelect, LocaleSelect } from '@/components/sites/environment-select';
import { useSiteScope } from '@/components/sites/site-scope';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { Button } from '@/components/ui/button';
import { messageForError } from '@/lib/api-client';
import { contentApi, contentKeys, entriesQuery } from '@/lib/api/content';
import { pagesQuery } from '@/lib/api/pages';
import type { ContentEntry, EntryInput } from '@/lib/api/types';
import { useOrgPermissions } from '@/lib/permissions';

function PageEditor() {
  const { orgId, siteId, pageId } = Route.useParams();
  const queryClient = useQueryClient();
  const { site, environments, environment, environmentId, setEnvironmentId, locale, setLocale } =
    useSiteScope();
  const { has } = useOrgPermissions(orgId);
  const canEdit = has(PERMISSIONS.CONTENT_EDIT);
  const canViewHistory = has(PERMISSIONS.CONTENT_VERSIONS_VIEW);

  const pages = useQuery(pagesQuery(orgId, siteId));
  const page = pages.data?.find((item) => item.id === pageId) ?? null;

  const [scanOpen, setScanOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState<ContentEntry | null>(null);

  const entries = useQuery({
    ...entriesQuery(orgId, siteId, { pageSlug: page?.slug ?? null, locale, environmentId }),
    enabled: page !== null,
  });

  const save = useCallback(
    (batch: EntryInput[]) => contentApi.saveEntries(orgId, siteId, batch),
    [orgId, siteId],
  );

  const onSaved = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: contentKeys.all(orgId, siteId) });
  }, [orgId, queryClient, siteId]);

  const liveUrl = useMemo(() => {
    if (!environment?.baseUrl || !page) return null;
    return `${environment.baseUrl.replace(/\/$/, '')}${page.slug === '/' ? '/' : page.slug}`;
  }, [environment?.baseUrl, page]);

  if (pages.isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
      >
        {messageForError(pages.error, 'This page could not be loaded.')}
      </div>
    );
  }

  if (!pages.isLoading && !page) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-data text-ink-muted">
          That page is not registered on this site any more.
        </p>
        <Button asChild variant="secondary" size="sm">
          <Link to="/app/$orgId/sites/$siteId/pages" params={{ orgId, siteId }}>
            <ArrowLeftIcon />
            Back to pages
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/app/$orgId/sites/$siteId/pages"
            params={{ orgId, siteId }}
            className="inline-flex items-center gap-1 text-micro text-ink-muted underline-offset-2 hover:text-ink hover:underline"
          >
            <ArrowLeftIcon className="size-3" />
            All pages
          </Link>
          <h2 className="text-lede font-semibold tracking-tight text-ink">
            {page?.name ?? 'Page'}
          </h2>
          {CONTENT_ONLY ? null : (
            <p className="font-mono text-micro text-ink-subtle">{page?.slug}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {liveUrl ? (
            <Button asChild variant="ghost" size="sm">
              <a href={liveUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLinkIcon />
                View live
              </a>
            </Button>
          ) : null}
          {CONTENT_ONLY ? null : (
            <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
              <Button variant="secondary" size="sm" onClick={() => setScanOpen(true)}>
                <ScanTextIcon />
                Scan this page
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>

      <ContentFormRenderer
        orgId={orgId}
        siteId={siteId}
        entries={entries.data ?? []}
        pageSlug={page?.slug ?? null}
        locale={locale}
        canEdit={canEdit}
        isLoading={pages.isLoading || entries.isLoading}
        error={entries.isError ? entries.error : undefined}
        onSave={save}
        onSaved={onSaved}
        onHistory={canViewHistory ? setHistoryEntry : undefined}
        toolbar={
          <>
            {CONTENT_ONLY ? null : (
              <EnvironmentSelect
                environments={environments}
                value={environmentId}
                onChange={setEnvironmentId}
                label="Status for"
              />
            )}
            <LocaleSelect locales={site.locales} value={locale} onChange={setLocale} />
          </>
        }
      />

      <ScanDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        orgId={orgId}
        siteId={siteId}
        pageSlug={page?.slug ?? null}
        canRegister={canEdit}
      />

      <VersionHistoryDrawer
        open={historyEntry !== null}
        onOpenChange={(open) => !open && setHistoryEntry(null)}
        orgId={orgId}
        siteId={siteId}
        entry={historyEntry}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/pages/$pageId')({
  component: PageEditor,
});
